'use client';

import { useEffect, useRef } from 'react';

// The AutoScan demo recording opens with a ~2s white load-in frame.
// Native loop replays that white frame on every cycle, which reads as a
// periodic flash. We loop manually back to LOOP_START so the seam always
// lands on the dark UI instead of the white intro.
const LOOP_START = 2.0;

export default function DemoVideo() {
  const ref = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const v = ref.current;
    if (!v) return;

    const restart = () => {
      try {
        v.currentTime = LOOP_START;
        const p = v.play();
        if (p && typeof p.catch === 'function') p.catch(() => {});
      } catch {
        /* ignore seek/play races */
      }
    };

    const skipIntro = () => {
      if (v.currentTime < LOOP_START) {
        try {
          v.currentTime = LOOP_START;
        } catch {
          /* ignore */
        }
      }
    };

    const onTimeUpdate = () => {
      if (v.duration && v.currentTime >= v.duration - 0.15) restart();
    };

    v.addEventListener('loadedmetadata', skipIntro);
    v.addEventListener('timeupdate', onTimeUpdate);
    v.addEventListener('ended', restart);
    if (v.readyState >= 1) skipIntro();

    return () => {
      v.removeEventListener('loadedmetadata', skipIntro);
      v.removeEventListener('timeupdate', onTimeUpdate);
      v.removeEventListener('ended', restart);
    };
  }, []);

  return (
    <video
      ref={ref}
      className="w-full aspect-video object-cover bg-surface-deep block brightness-[1.15] contrast-[1.07] saturate-[1.12]"
      autoPlay
      muted
      playsInline
      preload="metadata"
      poster="https://platform.cyberarmy.ai/demo/8-results.png"
      aria-label="AutoScan running a real agentic penetration test"
    >
      <source src="https://platform.cyberarmy.ai/demo/8-results.webm" type="video/webm" />
    </video>
  );
}
